import { Component, computed, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { ApiService, Table } from '../services/api.service';
import { QrStateService } from '../services/qr-state.service';
import { TableSelectModalComponent } from '../components/table-select-modal.component';

interface CartEntry { id: string; name: string; price: number; image: string; qty: number; note?: string; }

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [CommonModule, RouterLink, TableSelectModalComponent],
  template: `
    <main class="min-h-screen bg-gray-950 text-gray-100">
      <!-- header link -->
      <section class="px-4 pt-4">
        <a routerLink="/" class="inline-flex items-center gap-2 text-sm text-gray-300 hover:underline">
          <span class="material-symbols-outlined text-base">arrow_back</span> Zurück zur Speisekarte
        </a>
      </section>

      <section class="px-4 mt-2">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-xl font-semibold">Kasse</h1>
            @if (!qrState.isWaiterMode()) {
              <p class="text-sm text-gray-400">Überprüfe deine Auswahl</p>
            }
            @if (qrState.isWaiterMode()) {
              <p class="text-sm text-blue-400 font-medium">Bedienungs-Modus aktiv</p>
            }
          </div>
          @if (count() > 0) {
            <button (click)="clearCart()" class="text-sm text-red-400 hover:text-red-300 underline">Warenkorb leeren</button>
          }
        </div>
      </section>

      <!-- cart items list -->
      @if (list().length > 0) {
        <section class="px-4 mt-4">
          @for (it of list(); track it.id) {
            <div class="flex items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 p-3 mb-2">
              <!-- <img [src]="it.image" [alt]="it.name" class="h-12 w-12 rounded-md object-cover" /> -->
              <div class="flex-1 min-w-0">
                <div class="font-medium leading-tight truncate">{{ it.name }}</div>
                @if (it.note) {
                  <div class="text-xs italic text-gray-400">"{{ it.note }}"</div>
                }
                <div class="text-xs text-gray-400">{{ it.price | currency:'EUR':'symbol':'1.2-2' }} × {{ it.qty }} = {{ (it.price * it.qty) | currency:'EUR':'symbol':'1.2-2' }}</div>
              </div>
              <div class="flex items-center gap-2">
                <button class="rounded-md bg-gray-800 px-2 py-1" (click)="dec(it.id)">−</button>
                <span class="w-6 text-center">{{ it.qty }}</span>
                <button class="rounded-md bg-gray-800 px-2 py-1" (click)="inc(it.id)">＋</button>
              </div>
              <button class="ml-2 grid place-items-center rounded-md p-2 hover:bg-gray-800" title="Entfernen" (click)="remove(it.id)"><span class="material-symbols-outlined text-base">delete</span></button>
            </div>
          }
        </section>
      } @else {
        <section class="px-4 mt-6">
          <div class="rounded-lg border border-gray-800 bg-gray-900 p-4 text-sm text-gray-300">
            Dein Warenkorb ist leer.
          </div>
        </section>
      }


      <!-- table info -->
      <section class="px-4 mt-6">
        <div class="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-900 px-3 py-3">
          <div class="text-sm space-y-1">
            <div class="font-medium">
              {{ qrState.tableNumber() ? ('Tisch ' + qrState.tableNumber()) : (qrState.tableId() ? 'Tisch …' : 'Kein Tisch gewählt') }}
            </div>
            <div class="text-xs" [class.text-orange-400]="!qrState.tableId()" [class.text-gray-400]="!!qrState.tableId()">
              {{ qrState.tableId() ? 'Die Bestellung geht an diesen Tisch' : 'Bitte wähle einen Tisch für die Bestellung' }}
            </div>
          </div>
          @if (qrState.isWaiterMode() || !qrState.tableId()) {
            <button class="rounded-md bg-gray-800 px-3 py-1.5 text-sm" (click)="openTableModal()">{{ qrState.tableId() ? 'Ändern' : 'Wählen' }}</button>
          }
        </div>
      </section>

      <!-- email input -->
      @if (!qrState.isWaiterMode()) {
        <section class="px-4 mt-6">
          <label class="text-sm mb-1 block">E-Mail für Bestellbestätigung (optional)</label>
          <input type="email" [value]="email()" (input)="email.set(($any($event.target)).value)" placeholder="ihre@email.de"
            class="w-full rounded-md border border-gray-800 bg-gray-900 px-3 py-2 text-sm outline-none placeholder:text-gray-500" />
          <p class="mt-2 text-xs text-gray-500">Ihre E-Mail wird nur für die Bestellbestätigung verwendet.</p>
        </section>
      }

      <!-- tip selection (online payment only) -->
      @if (tipApplicable() && count() > 0) {
        <section class="px-4 mt-6">
          <label class="text-sm mb-1 block">Trinkgeld (optional)</label>
          <div class="flex flex-wrap gap-2">
            @for (p of tipPresets; track p) {
              <button
                (click)="selectTipPreset(p)"
                [class]="'rounded-md border px-3 py-2 text-sm transition ' +
                  (!customTipActive() && selectedTipPercent() === p
                    ? 'border-orange-500 bg-orange-500 text-white'
                    : 'border-gray-800 bg-gray-900 text-gray-300 hover:bg-gray-800')">
                {{ p === 0 ? 'Kein' : p + ' %' }}
              </button>
            }
            <button
              (click)="selectCustomTip()"
              [class]="'rounded-md border px-3 py-2 text-sm transition ' +
                (customTipActive()
                  ? 'border-orange-500 bg-orange-500 text-white'
                  : 'border-gray-800 bg-gray-900 text-gray-300 hover:bg-gray-800')">
              Eigener %
            </button>
          </div>
          @if (customTipActive()) {
            <div class="relative mt-2 w-36">
              <input type="number" min="0" max="100" step="0.5" inputmode="decimal"
                [value]="customTipValue()"
                (input)="customTipValue.set(($any($event.target)).value)"
                placeholder="z.B. 12"
                class="w-full rounded-md border border-gray-800 bg-gray-900 px-3 py-2 pr-8 text-sm outline-none placeholder:text-gray-500" />
              <span class="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">%</span>
            </div>
          }
          @if (tipAmount() > 0) {
            <p class="mt-2 text-xs text-gray-400">+ {{ tipAmount() | currency:'EUR':'symbol':'1.2-2' }} Trinkgeld – vielen Dank!</p>
          }
        </section>
      }

      <div class="h-24"></div>

      <!-- bottom bar -->
      @if (count() > 0) {
        <div class="fixed inset-x-4 bottom-4 z-50">
          <div class="rounded-xl border border-gray-800 bg-gray-900 px-3 py-3 shadow-lg flex items-center justify-between gap-3">
            <div class="flex-shrink-0">
              <div class="text-xs text-gray-400">Gesamtsumme</div>
              <div class="text-lg font-semibold">{{ grandTotal() | currency:'EUR':'symbol':'1.2-2' }}</div>
              @if (tipAmount() > 0) {
                <div class="text-[11px] text-gray-500">inkl. {{ tipAmount() | currency:'EUR':'symbol':'1.2-2' }} Trinkgeld</div>
              }
            </div>
            <button class="inline-flex items-center rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-400 disabled:opacity-60 disabled:cursor-not-allowed" (click)="submit()" [disabled]="submitting() || !qrState.tableId()">{{ submitButtonLabel() }}</button>
          </div>
        </div>
      }

      @if (showSummary()) {
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
          <div class="w-full max-w-md rounded-2xl border border-gray-800 bg-gray-950 p-6 shadow-2xl">
            <div class="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 p-5 text-gray-950">
              <button class="absolute right-3 top-3 text-gray-900/80 transition hover:text-gray-900" (click)="closeSummary()" aria-label="Modal schließen"><span class="material-symbols-outlined text-base">close</span></button>
              <div class="flex items-start gap-4">
                <div class="grid h-12 w-12 flex-shrink-0 place-items-center rounded-full bg-white/25 text-2xl"><span class="material-symbols-outlined text-2xl">check</span></div>
                <div>
                  <h2 class="text-xl font-semibold">Bestellung abgeschlossen!</h2>
                  <p class="mt-1 text-sm text-gray-900/80">{{ summaryMessage() }}</p>
                </div>
              </div>
            </div>
            <div class="mt-5">
              <h3 class="text-sm uppercase tracking-wide text-gray-500">Zusammenfassung</h3>
            </div>
            <div class="mt-3 max-h-64 space-y-3 overflow-y-auto pr-1">
              @for (it of summaryItems(); track it.id) {
                <div class="flex items-start justify-between rounded-lg border border-gray-800 bg-gray-900 px-3 py-2">
                  <div>
                    <div class="text-sm font-medium">{{ it.name }}</div>
                    <div class="text-xs text-gray-500">{{ it.qty }} × {{ it.price | currency:'EUR':'symbol':'1.2-2' }}</div>
                  </div>
                  <div class="text-sm font-semibold">{{ (it.qty * it.price) | currency:'EUR':'symbol':'1.2-2' }}</div>
                </div>
              }
            </div>
            <div class="mt-4 rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
              <div class="flex items-center justify-between text-sm">
                <span class="text-gray-400">Gesamtsumme</span>
                <span class="text-base font-semibold">{{ summaryTotal() | currency:'EUR':'symbol':'1.2-2' }}</span>
              </div>
              @if (summaryEmail()) {
                <div class="mt-2 text-xs text-gray-500">Bestätigung wird an {{ summaryEmail() }} gesendet.</div>
              }
            </div>
            <div class="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <a routerLink="/" class="w-full inline-flex items-center justify-center rounded-xl bg-orange-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-orange-400 sm:w-auto" (click)="closeSummary()">Zur Speisekarte</a>
            </div>
          </div>
        </div>
      }

      <!-- Table Selection Modal -->
      @if (showTableModal()) {
        <app-table-select-modal
          [tables]="availableTables()"
          [selectedTableId]="qrState.tableId()"
          (tableSelected)="selectTable($event)"
          (closed)="closeTableModal()" />
      }
    </main>
    `,
})
export class CheckoutComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  public qrState = inject(QrStateService);

  ngOnInit() {
    // Restore QR state after a page refresh directly on the checkout page
    this.qrState.initializeFromUrl(this.route);

    // Load event to check whether online payment is enabled
    const eventId = this.qrState.eventId();
    if (eventId) {
      this.api.getEvent(eventId).subscribe(event => {
        if (event && event.id) {
          this.onlinePayment.set(event.onlinePaymentEnabled ?? true);
        }
      });
      this.loadAvailableTables(eventId);
    }
  }

  private async loadAvailableTables(eventId: string) {
    try {
      const tables = await this.api.getAllTables(eventId).toPromise();
      this.availableTables.set(tables ?? []);

      // Resolve the display number for a table that was set via QR code (id only)
      const tableId = this.qrState.tableId();
      if (tableId && !this.qrState.tableNumber()) {
        const current = (tables ?? []).find(t => t.id === tableId);
        if (current) {
          this.qrState.setTableNumber(current.number);
        }
      }
    } catch (err) {
      console.error('Failed to load available tables:', err);
    }
  }

  // Record keyed by product id
  private items = signal<Record<string, { qty: number; price: number; name: string; image: string; note?: string }>>(
    (() => {
      try {
        const raw = localStorage.getItem('cart');
        return raw ? JSON.parse(raw) : {};
      } catch {
        return {} as Record<string, { qty: number; price: number; name: string; image: string; note?: string }>;
      }
    })()
  );

  // Flattened list for ngFor
  list = computed<CartEntry[]>(() => Object.entries(this.items()).map(([id, v]) => ({ id, name: v.name, price: v.price, image: v.image, qty: v.qty, note: v.note })).filter(x => x.qty > 0));
  count = computed(() => this.list().reduce((a, b) => a + b.qty, 0));
  total = computed(() => this.list().reduce((a, b) => a + b.qty * b.price, 0));

  email = signal('');
  submitting = signal(false);
  showSummary = signal(false);
  summaryItems = signal<CartEntry[]>([]);
  summaryTotal = signal(0);
  summaryEmail = signal<string | null>(null);
  summaryTableNumber = signal<string | null>(null);

  // Table selection
  availableTables = signal<Table[]>([]);
  showTableModal = signal<boolean>(false);

  // Online payment setting from event
  onlinePayment = signal(true);

  // Tipping (only relevant for online payment via Stripe)
  tipPresets = [0, 5, 10, 15];
  selectedTipPercent = signal(0);
  customTipActive = signal(false);
  customTipValue = signal('');

  tipApplicable = computed(() => this.onlinePayment() && !this.qrState.isWaiterMode());

  effectiveTipPercent = computed(() => {
    if (!this.tipApplicable()) return 0;
    if (this.customTipActive()) {
      const parsed = parseFloat(this.customTipValue().replace(',', '.'));
      if (isNaN(parsed)) return 0;
      return Math.min(100, Math.max(0, parsed));
    }
    return this.selectedTipPercent();
  });

  tipAmount = computed(() => Math.round(this.total() * this.effectiveTipPercent()) / 100);
  grandTotal = computed(() => this.total() + this.tipAmount());

  selectTipPreset(percent: number) {
    this.customTipActive.set(false);
    this.selectedTipPercent.set(percent);
  }

  selectCustomTip() {
    this.customTipActive.set(true);
  }

  submitButtonLabel = computed(() => {
    if (this.submitting()) return 'Sende ...';
    if (!this.qrState.tableId()) return 'Bitte Tisch wählen';
    return 'Bestellung abschicken';
  });

  summaryMessage = computed(() => {
    if (!this.qrState.isWaiterMode()) return 'Vielen Dank – wir bereiten deine Bestellung jetzt für dich vor.';
    const table = this.summaryTableNumber();
    return table ? `Bestellung für Tisch ${table} erfolgreich aufgenommen.` : 'Bestellung erfolgreich aufgenommen.';
  });

  private persist() {
    const snap = this.items();
    try {
      localStorage.setItem('cart', JSON.stringify(snap));
      localStorage.setItem('cartCount', String(this.count()));
    } catch {}
  }

  private handleOrderSuccess(snapshot: CartEntry[]) {
    // Show success summary
    this.summaryItems.set(snapshot);
    this.summaryTotal.set(this.total());
    this.summaryEmail.set(this.email() || null);
    this.summaryTableNumber.set(this.qrState.tableNumber());
    this.showSummary.set(true);

    // Clear cart
    this.items.set({});
    this.persist();

    if (this.qrState.isWaiterMode()) {
      this.qrState.clearTable();
    }
  }

  openTableModal() {
    this.showTableModal.set(true);
  }

  closeTableModal() {
    this.showTableModal.set(false);
  }

  selectTable(table: Table) {
    this.qrState.selectTable(table.id, table.number);
    this.showTableModal.set(false);
  }

  inc(id: string) {
    const c = { ...this.items() };
    const it = c[id];
    if (!it) return;
    c[id] = { ...it, qty: it.qty + 1 };
    this.items.set(c);
    this.persist();
  }

  dec(id: string) {
    const c = { ...this.items() };
    const it = c[id];
    if (!it) return;
    const next = Math.max(0, it.qty - 1);
    if (next === 0) delete c[id]; else c[id] = { ...it, qty: next };
    this.items.set(c);
    this.persist();
  }

  remove(id: string) {
    const c = { ...this.items() };
    delete c[id];
    this.items.set(c);
    this.persist();
  }

  clearCart() {
    if (confirm('Möchten Sie Ihren Warenkorb wirklich leeren?')) {
      this.items.set({});
      this.persist();
    }
  }

  async submit() {
    if (!this.qrState.eventId()) {
      alert('Kein Event-Kontext gefunden. Bitte QR-Code scannen.');
      return;
    }
    const snapshot = this.list();
    if (snapshot.length === 0) {
      return;
    }

    const tableId = this.qrState.tableId();
    if (!tableId) {
      alert('Kein Tisch gewählt. Bitte wähle zuerst einen Tisch.');
      return;
    }

    const checkoutItems = snapshot.map(it => ({
      productId: it.id.split('::')[0],
      quantity: it.qty,
      note: it.note,
      price: it.price
    }));

    // Log the items being sent for debugging
    console.log('Checkout items:', checkoutItems);
    console.log('Product IDs:', checkoutItems.map(it => it.productId));
    console.log('Online payment:', this.onlinePayment());

    const orderRequest = {
      tableId: tableId,
      email: this.email() || undefined,
      items: checkoutItems,
    };

    try {
      this.submitting.set(true);

      const companyId = this.qrState.companyId();
      const eventId = this.qrState.eventId();
      const waiterToken = this.qrState.waiterToken();
      const isWaiterMode = this.qrState.isWaiterMode();

      if (isWaiterMode && companyId && eventId && waiterToken) {
        // Waiter Mode: Always direct order via waiter endpoint
        const order = await this.api.createWaiterOrder(companyId, eventId, orderRequest, waiterToken).toPromise();
        console.log('Waiter order created:', order);

        if (order && order.id) {
          this.handleOrderSuccess(snapshot);
        } else {
          throw new Error('Bestellung konnte nicht erstellt werden');
        }
      } else if (this.onlinePayment()) {
        // Use Stripe Checkout; the backend recalculates prices and adds the tip as a line item
        const res = await this.api.createCheckoutSession(this.qrState.eventId()!, {
          ...orderRequest,
          tipPercent: this.effectiveTipPercent() || undefined,
        }).toPromise();
        console.log('Checkout session created:', res);

        if (res && res.url) {
          // Redirect to Stripe Checkout
          window.location.href = res.url;
        } else {
          throw new Error('Keine Checkout-URL erhalten');
        }
      } else {
        // Direct order creation (online payment disabled)
        const order = await this.api.createOrder(this.qrState.eventId()!, orderRequest).toPromise();
        console.log('Order created:', order);

        if (order && order.id) {
          this.handleOrderSuccess(snapshot);
        } else {
          throw new Error('Bestellung konnte nicht erstellt werden');
        }
      }
      this.submitting.set(false);
    } catch (e: any) {
      console.error('Checkout/Order failed', e);

      // Provide more specific error messages based on the error type
      let errorMessage = 'Fehler beim Erstellen der Bestellung. Bitte versuchen Sie es erneut.';

      if (e?.status === 404) {
        errorMessage = 'Der Bestelldienst ist nicht verfügbar. Bitte kontaktieren Sie das Personal.';
        console.error('Backend endpoint not found. Is the backend server running?');
      } else if (e?.status === 500) {
        errorMessage = 'Serverfehler beim Erstellen der Bestellung. Bitte versuchen Sie es erneut oder kontaktieren Sie das Personal.';
      } else if (e?.status === 400) {
        // Check if error is about a product not found
        const errorText = e?.error?.message || e?.error?.error || '';
        if (errorText.includes('product') && errorText.includes('not found')) {
          errorMessage = 'Ein oder mehrere Produkte in Ihrem Warenkorb sind nicht mehr verfügbar. Bitte leeren Sie Ihren Warenkorb und versuchen Sie es erneut.';
          console.error('Invalid product in cart. Use "Warenkorb leeren" to clear the cart.');
        } else if (errorText.includes('stripe payment')) {
          errorMessage = 'Für dieses Event ist Online-Zahlung erforderlich. Bitte verwenden Sie die Stripe-Zahlungsmethode.';
        } else {
          errorMessage = 'Ungültige Bestelldaten. Bitte überprüfen Sie Ihre Auswahl.';
        }
        if (e?.error?.message) {
          console.error('Backend validation error:', e.error.message);
        }
      } else if (e?.status === 0) {
        errorMessage = 'Keine Verbindung zum Server. Bitte überprüfen Sie Ihre Internetverbindung.';
      } else if (e?.message === 'Keine Checkout-URL erhalten') {
        errorMessage = 'Der Server hat keine Zahlungs-URL zurückgegeben. Bitte versuchen Sie es erneut.';
      } else if (e?.message === 'Bestellung konnte nicht erstellt werden') {
        errorMessage = 'Die Bestellung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.';
      }

      alert(errorMessage);
      this.submitting.set(false);
    }
  }

  closeSummary() {
    this.showSummary.set(false);
    this.summaryItems.set([]);
    this.summaryTotal.set(0);
    this.summaryEmail.set(null);
    this.summaryTableNumber.set(null);
  }
}
